import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, Form, DatePicker } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';
import UserSelector from '../../components/UserSelector';

const statusMap = {
    0: { text: '未开始', type: 'default' },
    1: { text: '进行中', type: 'primary' },
    2: { text: '已完成', type: 'success' },
    4: { text: '已取消', type: 'danger' },
};


const Meetings = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [formApi, setFormApi] = useState(null);
    const [typ, setTyp] = useState(1);

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/o2o/list_meeting/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    typ: typ !== null ? typ : undefined,
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const urlTyp = searchParams.get('typ');
        setTyp(urlTyp ? parseInt(urlTyp) : 1);
    }, []);

    useEffect(() => {
        if (typ !== null) {
            fetchList();
        }
    }, [typ]);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/o2o/add_meeting/', { ...values, typ });
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/o2o/edit_meeting/', {
                ...values,
                uid: currentRecord.uid,
                typ: currentRecord.typ,
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/o2o/delete_meeting/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的会议');
            return;
        }
        try {
            const response = await request.post('/go/sp/o2o/batchedit_meeting/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '会议名称',
            dataIndex: 'title',
            width: 200,
        },
        {
            title: '发起人',
            dataIndex: 'su',
            width: 120,
            render: (su) => su?.name || su?.nickname || su?.uid || '-'
        },
        {
            title: '地点',
            dataIndex: 'addr',
            width: 180,
        },
        {
            title: '开始时间',
            dataIndex: 'start_time',
            width: 180,
            render: (text) => formatTimestamp(text)
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '创建/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该会议吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';
        const initialValues = isEdit && currentRecord ? {
            ...currentRecord,
            start_time: currentRecord.start_time ? new Date(parseInt(currentRecord.start_time)) : '',
            content: currentRecord.content || '{}',
        } : {
            su_uid: '',
            title: '',
            addr: '',
            start_time: '',
            status: 0,
            content: '{}',
        };

        return (
            <Form
                getFormApi={setFormApi}
                initValues={initialValues}
                onSubmit={values => {
                    const submitValues = {
                        ...values,
                        start_time: values.start_time ? (typeof values.start_time === 'string' ? values.start_time : values.start_time.getTime().toString()) : '',
                    };
                    if (isEdit) {
                        handleEdit(submitValues);
                    } else {
                        handleAdd(submitValues);
                    }
                }}
                style={{ padding: '20px' }}
            >
                <UserSelector
                    value={initialValues.su_uid}
                    onChange={val => formApi && formApi.setValue('su_uid', Array.isArray(val) ? val[0] : val)}
                    placeholder="请选择发起人"
                    multiple={false}
                />
                <Form.Input
                    field="title"
                    label="会议名称"
                    rules={[{ required: true, message: '请输入会议名称' }]}
                />
                <Form.Input
                    field="addr"
                    label="地点"
                    rules={[{ required: true, message: '请输入地点' }]}
                />
                <Form.DatePicker
                    field="start_time"
                    label="开始时间"
                    type="dateTime"
                    rules={[{ required: true, message: '请选择开始时间' }]}
                />
                <Form.Select
                    field="status"
                    label="状态"
                    rules={[{ required: true, message: '请选择状态' }]}
                >
                    <Select.Option value={0}>未开始</Select.Option>
                    <Select.Option value={1}>进行中</Select.Option>
                    <Select.Option value={2}>已完成</Select.Option>
                    <Select.Option value={4}>已取消</Select.Option>
                </Form.Select>
                <Form.TextArea
                    field="content"
                    label="内容(JSON)"
                    placeholder="{link: , records: , users: []}"
                />
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    return (
        <div className={styles.couponsContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索会议名称"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="会议状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增会议</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' onClick={() => handleBatchEdit(4, selectedRowKeys)}>批量取消</Button>
                            <Button type='primary' onClick={() => handleBatchEdit(0, selectedRowKeys)}>批量设为未开始</Button>
                            <Button type='success' onClick={() => handleBatchEdit(2, selectedRowKeys)}>批量设为已完成</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个会议吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增会议"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('add')}
            </Modal>

            <Modal
                title="编辑会议"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('edit')}
            </Modal>
        </div>
    );
};

export default Meetings; 